/**
 * Cuántos productos y cuántas variantes agarra el alcance que se está armando.
 *
 * *** El número tiene que estar en el paso 1, no en el 3. *** Antes el comerciante elegía
 * el alcance, pasaba al paso 2, cargaba un porcentaje y recién en el 3 se enteraba de que
 * la regla no agarraba nada. Dos pasos perdidos para llegar a un cero.
 *
 * Del otro lado hay un barrido del catálogo —no hay filtro por categoría ni por marca en la
 * API de Tienda Nube, así que contar es leer—, y ese rate limit lo comparte el comerciante
 * con todas sus otras apps. Tres cosas para no gastarlo al pedo:
 *
 *  - **Rebote de 600 ms.** Tildar tres categorías seguidas es una llamada, no tres.
 *  - **Cache por alcance, para toda la sesión.** Ir y volver entre los pasos 1 y 2, o
 *    destildar y volver a tildar, no vuelve a salir a la red.
 *  - **Nada de contar sin criterio.** Un alcance vacío no se puede ejecutar; tampoco se
 *    cuenta.
 *
 * *** Y nunca bloquea. *** El botón "Siguiente" no espera este número. Si tarda, si falla o
 * si el catálogo no entra en el presupuesto de barrido, la pantalla sigue funcionando
 * exactamente igual: el conteo es una ayuda, y una ayuda que frena el avance deja de serlo.
 */

import { useEffect, useState } from 'react';
import { api, type Alcance } from './api';

export type EstadoDelConteo =
  /** El alcance todavía no define ningún criterio. No hay nada que contar. */
  | { readonly clase: 'sin_criterio' }
  | { readonly clase: 'contando' }
  | { readonly clase: 'listo'; readonly productos: number; readonly variantes: number }
  /** Falló, o el catálogo se pasó del presupuesto. No se muestra nada. */
  | { readonly clase: 'desconocido' };

const REBOTE_MS = 600;

/**
 * Cache de sesión, fuera del componente a propósito: sobrevive a ir y volver entre pasos y
 * a salir del wizard y entrar de nuevo. El catálogo puede cambiar mientras tanto, y es un
 * riesgo aceptado — este número orienta, el que decide es el preview, que siempre relee.
 */
const cache = new Map<string, { readonly productos: number; readonly variantes: number }>();

/** La firma del alcance. Dos alcances con la misma firma dan el mismo conteo. */
function clave(alcance: Alcance): string {
  return JSON.stringify(alcance);
}

/** `true` si el alcance no define ningún criterio de inclusión. Espejo de `sinCriterio`. */
function vacio(alcance: Alcance): boolean {
  return (
    !alcance.allProducts &&
    alcance.categoryIds.length === 0 &&
    alcance.brands.length === 0 &&
    alcance.priceMin === null &&
    alcance.priceMax === null
  );
}

export function useConteoDeAlcance(alcance: Alcance): EstadoDelConteo {
  const llave = clave(alcance);
  const [estado, setEstado] = useState<EstadoDelConteo>({ clase: 'sin_criterio' });

  useEffect(() => {
    if (vacio(alcance)) {
      setEstado({ clase: 'sin_criterio' });
      return;
    }

    const guardado = cache.get(llave);
    if (guardado !== undefined) {
      setEstado({ clase: 'listo', ...guardado });
      return;
    }

    setEstado({ clase: 'contando' });

    let vigente = true;
    const temporizador = setTimeout(() => {
      api
        .alcance(alcance)
        .then(({ productos, variantes }) => {
          if (!vigente) return;
          if (productos === null || variantes === null) {
            setEstado({ clase: 'desconocido' });
            return;
          }
          cache.set(llave, { productos, variantes });
          setEstado({ clase: 'listo', productos, variantes });
        })
        .catch(() => {
          // Un conteo que falla no es un error del comerciante y no tiene nada que hacer al
          // respecto: la pantalla se queda sin el número y sigue andando.
          if (vigente) setEstado({ clase: 'desconocido' });
        });
    }, REBOTE_MS);

    return () => {
      vigente = false;
      clearTimeout(temporizador);
    };
    // `llave` es la identidad del alcance: sin esto, cada render con un objeto nuevo pero
    // igual dispararía otra llamada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llave]);

  return estado;
}
